import { cancellableFetch } from './async/cancellable-fetch'
import { auth } from './auth'

const baseUrl = (() => {
    const hostname = window && window.location && window.location.hostname

    if (hostname && (hostname.includes('staging') || hostname.includes('testing'))) {
        return 'https://webapi.testing.budbee.com/'
    }

    if (hostname && hostname.includes('production')) {
        return 'https://webapi.budbee.com/'
    }

    // Fallback to environment variable if no matching conditions
    return process.env.VUE_APP_WEBAPI_URL
})()

const parse = (response) => {
    if (response.ok) {
        return response.json()
    }

    const error = new Error(response.statusText)
    error.response = response
    throw error
}

function getUrlWithQueryParams(url, query) {
    return `${baseUrl + url.join('/')}?${query.join('&')}`
}

function getUrl(url) {
    return baseUrl + url.join('/')
}

const request = (method, headers, url, queryParams, body) => {
    const options = {
        method,
        mode: 'cors',
        cache: 'no-cache',
        // credentials: 'include',
        headers,
        redirect: 'follow',
        referrer: 'no-referrer',
        body,
        queryParams,
    }

    const query = Object.keys(queryParams).reduce((q, key) => {
        const values = queryParams[key]
        values.forEach((val) => q.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`))
        return q
    }, [])

    const actualUrl = query.length > 0 ? getUrlWithQueryParams(url, query) : getUrl(url)
    return cancellableFetch(actualUrl, options).then(parse)
}

export const Webapi = (
    method,
    headers = [['authorization', auth.get()]],
    url = [],
    queryParams = [],
    body,
) => ({
    routes() {
        this.contentType('application/json').path('trafiki').path('routes').get()
    },

    route(routeId) {
        return this.path('trafiki').path('routes').path(routeId).get()
    },

    locker(lockerId) {
        return this.path('lockers').path(lockerId).path('box-utilisation').get()
    },

    routeComments(routeId) {
        return this.path('trafiki').path('routes').path(routeId).path('comments').get()
    },

    addComment(routeId, comment) {
        return this.contentType('application/json')
            .path('trafiki')
            .path('routes')
            .path(routeId)
            .path('comments')
            .post({
                comment,
                userId: 0,
                routeId,
            })
    },

    courierDrivers() {
        return this.path('trafiki').path('courier-drivers').get()
    },

    cancelConsignments(consignmentIds, comment, reason) {
        return this.contentType('application/json')
            .path('trafiki')
            .path('consignments-cancellation')
            .post({
                consignmentIds,
                comment,
                reason,
            })
    },

    splitRoute(
        routeId,
        stopIds,
        crossdockSequence,
        reason,
        serviceTime,
        vehicleType,
        responsibleParty,
        driver,
    ) {
        return this.contentType('application/json')
            .path('trafiki')
            .path('route-split')
            .post({
                splitRequest: {
                    originRouteId: routeId,
                    stopIds,
                    splitStopSequence: crossdockSequence,
                    serviceTime,
                    vehicleType,
                    reason,
                    responsibleParty,
                },
                driverToAssign: driver,
            })
    },

    completeParcelsAtStop(routeId, stopId, parcelIds, comment, verificationCode) {
        return this.contentType('application/json')
            .path('trafiki')
            .path('complete-parcels-at-stop')
            .put({
                routeId,
                stopId,
                parcelIds,
                comment,
                verificationCode,
            })
    },

    uiData() {
        return this.path('trafiki').path('ui-settings').get()
    },

    returnPallets(routeId, driverId) {
        return this.path('trafiki')
            .path('return-pallets')
            .path(routeId)
            .queryParam('driverId', driverId)
            .get()
    },

    pickupHistory(routeId) {
        return this.path('trafiki').path('pickup-history').path(routeId).get()
    },

    path(part) {
        return Webapi(method, headers, [...url, part], queryParams, body)
    },

    queryParam(key, value) {
        const updatedQueryParams = { ...queryParams }
        if (updatedQueryParams[key] == null) {
            updatedQueryParams[key] = []
        }
        updatedQueryParams[key].push(value)
        return Webapi(method, headers, url, updatedQueryParams, body)
    },

    header(key, value) {
        return Webapi(method, [...headers, [key, value]], url, queryParams, body)
    },

    authorization(value) {
        return this.header('authorization', value)
    },

    contentType(value) {
        return this.header('content-type', value).header('accept', value)
    },

    get() {
        return request('get', headers, url, queryParams)
    },

    post(payload) {
        return request('post', headers, url, queryParams, JSON.stringify(payload))
    },

    put(payload) {
        return request('put', headers, url, queryParams, JSON.stringify(payload))
    },

    delete() {
        return request('delete', headers, url, queryParams, body)
    },
})
